import React from 'react';
import axios from 'axios';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { TFunction } from 'i18next';
import { IModalProps } from 'store/modal';
import Button from '../../../components/Button';

// Sign process used in "employer" mode.
// QES(Qualified Electronic Signature) on a physical medium ("flash drive").
//
// The BISS desktop application is used to extract the certificate and
// the signature from the "flash drive".
//
// Sign process takes place with 4 requests:
// two to the desktop application BISS and two to BE.
export const signDocAsEmployer = async (
  docId: string,
  docHolderId: string,
  showModal: (props: IModalProps) => void,
  hideModal: () => void,
  invalidate: () => void,
  successMsgKey: string,
  t: TFunction
) => {
  const handleErrorModal = (msg: string) => {
    showModal({
      type: 'error',
      content: msg,
    });
  };

  // 1. Certificate selection - request to BISS
  const req1 = await axios
    .post(`${process.env.REACT_APP_API_URL_LOCALHOST}getsigner`, {
      selector: {},
      showAllCerts: false,
      showValidCerts: true,
    })
    .catch(error => {
      if (error.response) {
        // The request was made and the server responded with error status code
        handleErrorModal(
          error.response.data.reasonText
            ? `${error.response.data.reasonText}, ${t('biss.checkOption')}`
            : t('biss.generalError')
        );
      } else if (error.request) {
        // The request was made but no response was received
        handleErrorModal(t('biss.noResponse'));
      } else {
        handleErrorModal(`${error.message}`);
      }
    });

  if (!req1) return;

  // 2. Digest calculation - request to BE
  const req2 = await API.post(`/documents/${docId}/sign-qes-digest`, {
    user_certificate: req1.data.chain[0],
  }).catch(response => {
    const error = createErrorFromResponse(response);
    handleErrorModal(error.message);
  });

  if (!req2) return;

  // 3. Signature generation - request to BISS
  const req3 = await axios.post(`${process.env.REACT_APP_API_URL_LOCALHOST}sign`, { ...req2.data }).catch(error => {
    if (error.response) {
      handleErrorModal(
        error.response.data.reasonText
          ? `${error.response.data.reasonText}, ${t('biss.checkOption')}`
          : t('biss.generalError')
      );
    } else if (error.request) {
      handleErrorModal(t('biss.noResponse'));
    } else {
      handleErrorModal(`${error.message}`);
    }
  });

  if (!req3) return;

  // 4. To insert the signature in the PDF document - request to BE
  const req4 = await API.post(`/documents/${docId}/sign-qes-document`, {
    user_certificate: req1.data.chain[0],
    signatures: req3.data.signatures,
    digest_times: req2.data.extra.digestTimes,
  }).catch(response => {
    const error = createErrorFromResponse(response);
    handleErrorModal(error.message);
  });

  if (!req4) return;

  // Show success message and invalidate
  showModal({
    type: 'success',
    content: t(successMsgKey),
    onSuccess: () => {
      const { message } = req4.data;
      if (message) {
        const { type, body } = message;
        if (type === 'approve_sign')
          showModal({
            type: 'success',
            heading: t('documents.table.notificationSent'),
            content: body,
            successButton: (
              <Button
                btnType="primary"
                onClick={() => {
                  hideModal();
                }}
              >
                {t('common.ok').toString()}
              </Button>
            ),
          });

        if (type === 'send_to_employee') sendToEmployee(docHolderId, body, showModal, invalidate, t);

        if (type === 'archive') archiveDoc(docHolderId, body, showModal, invalidate, t);
      }
    },
  });
  invalidate();
};

export const sendToEmployee = (
  docHolderId: string,
  message: string,
  showModal: (props: IModalProps) => void,
  invalidate: () => void,
  t: TFunction
) => {
  showModal({
    type: 'confirm',
    heading: t('documents.table.confirmSendDocHeading'),
    content: message,
    onConfirm: () =>
      API.post(`document-holders/${docHolderId}/send-to-employee`)
        .then(() => {
          showModal({
            type: 'success',
            content: t('documents.table.sentDocSuccessMsg'),
          });
          invalidate();
        })
        .catch(err =>
          showModal({
            type: 'error',
            content: createErrorFromResponse(err).message,
          })
        ),
    confirmLabel: t('documents.table.confirmSendDocLabel'),
  });
};

export const sendToEmployer = (
  docHolderId: string,
  message: string,
  showModal: (props: IModalProps) => void,
  invalidate: () => void,
  t: TFunction
) => {
  showModal({
    type: 'confirm',
    heading: t('documents.table.confirmSendDocHeading'),
    content: message,
    onConfirm: () =>
      API.post(`document-holders/${docHolderId}/send-to-employer`)
        .then(() => {
          showModal({
            type: 'success',
            content: t('documents.table.sentDocSuccessMsg'),
          });
          invalidate();
        })
        .catch(err =>
          showModal({
            type: 'error',
            content: createErrorFromResponse(err).message,
          })
        ),
    confirmLabel: t('documents.table.confirmSendDocLabel'),
  });
};

export const archiveDoc = (
  docHolderId: string,
  message: string,
  showModal: (props: IModalProps) => void,
  invalidate: () => void,
  t: TFunction
) => {
  showModal({
    type: 'confirm',
    heading: t('documents.table.confirmArchiveDocHeading'),
    content: message,
    onConfirm: () =>
      API.post(`document-holders/${docHolderId}/archive`)
        .then(() => {
          showModal({
            type: 'success',
            content: t('documents.table.archivedDocSuccessMsg'),
          });
          invalidate();
        })
        .catch(err =>
          showModal({
            type: 'error',
            content: createErrorFromResponse(err).message,
          })
        ),
    confirmLabel: t('documents.table.confirmArchiveDocLabel'),
  });
};

// Multi sign process used in "employer" mode.
// Analogical to signing single document as employer process.
// Difference is here we pass document holder ids to sign multiple documents
// inside the document holder that are available for signing and the user can sign
// (Checks which documents the user can sign are handled on BE)
//
// Sign process takes place with 4 requests:
// two to the desktop application BISS and two to BE.
export const multiSignDocAsEmployer = async (
  docHolderIds: number[],
  showModal: (props: IModalProps) => void,
  invalidate: () => void,
  successMsgKey: string,
  t: TFunction
) => {
  const handleErrorModal = (msg: string) => {
    showModal({
      type: 'error',
      content: msg,
    });
  };

  // 1. Certificate selection - request to BISS
  const req1 = await axios
    .post(`${process.env.REACT_APP_API_URL_LOCALHOST}getsigner`, {
      selector: {},
      showAllCerts: false,
      showValidCerts: true,
    })
    .catch(error => {
      if (error.response) {
        handleErrorModal(
          error.response.data.reasonText
            ? `${error.response.data.reasonText}, ${t('biss.checkOption')}`
            : t('biss.generalError')
        );
      } else if (error.request) {
        handleErrorModal(t('biss.noResponse'));
      } else {
        handleErrorModal(`${error.message}`);
      }
    });

  if (!req1) return;

  // 2. Digest calculation - request to BE
  const req2 = await API.post('document-holders/sign-multiple-digest', {
    user_certificate: req1.data.chain[0],
    document_holder_ids: docHolderIds,
  }).catch(response => {
    const error = createErrorFromResponse(response);
    handleErrorModal(error.message);
  });

  if (!req2) return;

  // 3. Signature generation - request to BISS
  const req3 = await axios.post(`${process.env.REACT_APP_API_URL_LOCALHOST}sign`, { ...req2.data }).catch(error => {
    if (error.response) {
      handleErrorModal(
        error.response.data.reasonText
          ? `${error.response.data.reasonText}, ${t('biss.checkOption')}`
          : t('biss.generalError')
      );
    } else if (error.request) {
      handleErrorModal(t('biss.noResponse'));
    } else {
      handleErrorModal(`${error.message}`);
    }
  });

  if (!req3) return;

  // 4. To insert the signature in the PDF document - request to BE
  const req4 = await API.post('document-holders/sign-multiple-document', {
    document_holder_ids: docHolderIds,
    user_certificate: req1.data.chain[0],
    signatures: req3.data.signatures,
    digest_times: req2.data.extra.digestTimes,
  }).catch(response => {
    const error = createErrorFromResponse(response);
    handleErrorModal(error.message);
  });

  if (!req4) return;

  // Show success message and invalidate
  const { message } = req4.data;
  if (message) {
    const { type, body } = message;
    if (type === 'multi_sign')
      showModal({
        type: 'success',
        content: body,
      });
  }
  invalidate();
};
